/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from "@angular/router";

import {EmGroup} from "../../models/em-group";
import {GroupType} from "../../models/group-type";
import {InstanceData} from "../../models/instance-data";
import {LocationInstance} from "../../models/location-instance";
import {Zone} from "../../models/zone";
import {ZoneInstance} from "../../models/zone-instance";

import {GroupService} from "../../services/group.service";
import { LocationService } from "../../services/location.service";
import {ZoneService} from "../../services/zone.service";

import {AddToGroupDialogComponent} from "../add-to-group-dialog.component";
import {ConfirmDeleteDialogComponent} from "../confirm-delete-dialog.component";
import {ManageGroupsDialogComponent} from "../manage-groups-dialog.component";
import {RemoveFromGroupDialogComponent} from "../remove-from-group-dialog.component";
import {ShowGroupDialogComponent} from "../show-group-dialog.component";

import {EntityInstanceDialogComponent} from './dialogs/entity-instance-dialog.component';
import {EntityTypeDialogComponent} from "./dialogs/entity-type-dialog.component";
import {INFRA_DUMMY_FUNCTION, PAGING_ITEMS_PER_PAGE, RESULTS_PER_PAGE} from "../../app-constants";
import { PagedResults } from 'app/models/paged-results';
import { NgxSpinnerService } from 'ngx-spinner';
import { Filter } from 'createjs-module';
import { FunctionParameterDataUi } from './dialogs/general-function-dialog.component';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';
import { FilterOption, FilterType, REMOVE_FILTER } from '../../models/filter-option';


@Component({
  templateUrl: './zones.component.html',
  styleUrls: ['../../../assets/css/infrastructure.css']
})
export class ZonesComponent implements OnInit {

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild('showGroupDialog')
  mShowGroupDialog: ShowGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  mManageGroupsDialog: ManageGroupsDialogComponent;

  @ViewChild('addToGroupDialog')
  mAddToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  mRemoveFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('entityTypeDialog')
  mEntityTypeDialogComponent: EntityTypeDialogComponent;

  @ViewChild('entityInstanceDialog')
  mEntityInstanceDialogComponent: EntityInstanceDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  mZones: ZoneUI[] = [];
  mZoneInstances: ZoneInstanceUI[] = [];

  mDeleteFlag = 0; //1 = Zones, 2 = Tag Instances


  //Entity Type Dialog Variables
  mEntity: Zone;
  mEntityName: string;
  mEntityType = GroupType.READZONE;
  mEntityTypeName = "Zone";

  // All the Filter Options available
  filterOptionForZones: FilterOption[] = [];
  filterOptionForZoneInstances: FilterOption[] = [];

  // The Filter Option Selected by the user
  selectedFilterOptionForZone: FilterOption = REMOVE_FILTER;
  selectedFilterOptionForZoneInstance: FilterOption = REMOVE_FILTER;
  /** Active filters for the Zone Instances (Name + Type), AND'd together */
  activeFilterOptionsForZoneInstance: FilterOption[] = [];

  //Entity Instance Dialog Variables
  mInstance: ZoneInstance;
  mInstanceData: InstanceData[];
  mInstanceName: string;
  mInstanceEntityType: Zone;
  mInstanceType = GroupType.READZONEINSTANCE;
  mInstanceTypeName = "Zone";
  mAvailableEntityTypes: Zone[];
  mSelectedLocation: LocationInstance;
  mAvailableLocations: LocationInstance[];

  currentPageForZones = 1;
  currentPageForZoneInstances = 1;

  totalNumberOfZones = 0;
  totalNumberOfZoneInstances = 0;

  // List for Dropdown of Results Per Page value
  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForTypes: number = this.resultsPerPageValues[0];
  selectedPageSizeValueForInstances: number = this.resultsPerPageValues[0];

  // Variables for the dropdown menu 
  functionList = [];
  selectedFunction = [];
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variable for General Confirm/Cancel Dialog Box
  showGeneralDialog: boolean = false;
  functionParameterData: FunctionParameterDataUi[] = [];

  constructor(
    private mRoute: ActivatedRoute,
    private mZoneService: ZoneService,
    private mGroupService: GroupService,
    private mLocationService: LocationService,
    private cdr: ChangeDetectorRef,
    private spinnerService: NgxSpinnerService
  ) {}



  ngOnInit(): void {
    this.refreshFilterOptionsForZones();
    this.refreshFilterOptionsForZoneInstances();
    this.selectedPageSizeValueForTypes = this.resultsPerPageValues[0];
    this.selectedPageSizeValueForInstances = this.resultsPerPageValues[0];

    this.mRoute.data.subscribe((
      data: { 
        zones: PagedResults<Zone>, 
        zoneInstances: PagedResults<ZoneInstance> 
      } ) => {
      this.updatePagedZones(data.zones);
      this.updatePagedZoneInstances(data.zoneInstances);
      this.mAvailableEntityTypes = data.zones.data;
    });
  }

  

  private updatePagedZones(zones: PagedResults<Zone>): void {
    const uiObjects: ZoneUI[] = [];
    if (zones.data != null) {
      zones.data.forEach(zone => {
        const ui = new ZoneUI();
        ui.mZone = zone;
        ui.mSelected = false;
        uiObjects.push(ui);
      });
    }
    this.mZones = uiObjects;
    this.totalNumberOfZones = zones.size;
  }

  private updatePagedZoneInstances(zoneInstances: PagedResults<ZoneInstance>): void {
    const uiObjects: ZoneInstanceUI[] = [];
    if (zoneInstances.data != null) {
      zoneInstances.data.forEach(zoneInstance => {
          const ui = new ZoneInstanceUI();
          ui.mZoneInstance = zoneInstance;
          ui.mSelected = false;
          uiObjects.push(ui);
      });
    }
    this.mZoneInstances = uiObjects;
    this.totalNumberOfZoneInstances = zoneInstances.size
  }



  isSingleZoneSelected(): boolean {
    if (this.mZones != null && this.mZones.length > 0) {
      let oneSelected = false;
      for (const zone of this.mZones) {
        if (zone.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyZonesSelected(): boolean {
    return this.mZones.some(zone => zone.mSelected);
  }

  isAllZonesSelected(): boolean {
    return this.mZones !== null && this.mZones.every(zone => zone.mSelected);
  }

  setAllZonesSelected(event: any): void {
    this.mZones.forEach(zone => zone.mSelected = event.target.checked);
  }





  isSingleZoneInstanceSelected(): boolean {
    if (this.mZoneInstances != null && this.mZoneInstances.length > 0) {
      let oneSelected = false;
      for (const zoneInstance of this.mZoneInstances) {
        if (zoneInstance.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyZoneInstancesSelected(): boolean {
    return this.mZoneInstances.some(zoneInstance => zoneInstance.mSelected);
  }

  isAllZoneInstancesSelected(): boolean {
    return this.mZoneInstances !== null && this.mZoneInstances.every(zoneInstance => zoneInstance.mSelected);
  }

  setAllZoneInstancesSelected(event: any): void {
    this.mZoneInstances.forEach(zoneInstance => zoneInstance.mSelected = event.target.checked);
  }





  getSelectedZones(): Zone[] {
    const zones: Zone[] = [];
    this.mZones.filter(ui => ui.mSelected).forEach(ui => zones.push(ui.mZone));
    return zones;
  }

  getSelectedZoneIds(): number[] {
    const ids: number[] = [];
    this.mZones.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mZone.id));
    return ids;
  }

  getSelectedZoneNames(): string[] {
    const names: string[] = [];
    this.mZones.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mZone.name));
    return names;
  }





  getSelectedZoneInstances(): ZoneInstance[] {
    const zoneInstances: ZoneInstance[] = [];
    this.mZoneInstances.filter(ui => ui.mSelected).forEach(ui => zoneInstances.push(ui.mZoneInstance));
    return zoneInstances;
  }

  getSelectedZoneInstanceIds(): number[] {
    const ids: number[] = [];
    this.mZoneInstances.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mZoneInstance.id));
    return ids;
  }

  getSelectedZoneInstanceNames(): string[] {
    const names: string[] = [];
    this.mZoneInstances.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mZoneInstance.name));
    return names;
  }





  manageZoneGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.READZONE);
  }

  manageZoneInstanceGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.READZONEINSTANCE);
  }





  addSelectedZonesToGroupClicked(): void {
    if (this.isAnyZonesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.READZONE, this.getSelectedZoneNames());
    }
  }

  addSelectedZoneInstancesToGroupClicked(): void {
    if (this.isAnyZoneInstancesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.READZONEINSTANCE, this.getSelectedZoneInstanceNames());
    }
  }

  addSelectedToGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.READZONE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedZoneIds())
          .then(
            success => {
              this.refreshZones();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.READZONEINSTANCE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedZoneInstanceIds())
          .then(
            success => {
              this.refreshZoneInstances();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  removeSelectedZonesFromGroupClicked(): void {
    if (this.isAnyZonesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const zoneNames: string[] = [];
      this.getSelectedZones().forEach(zone => {
        zoneNames.push(zone.name);
        if (zone.groups != null) {
          zone.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, zoneNames);
    }
  }

  removeSelectedZoneInstancesFromGroupClicked(): void {
    if (this.isAnyZoneInstancesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const zoneInstanceNames: string[] = [];
      this.getSelectedZoneInstances().forEach(zoneInstance => {
        zoneInstanceNames.push(zoneInstance.name);
        if (zoneInstance.groups != null) {
          zoneInstance.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, zoneInstanceNames);
    }
  }

  removeSelectedFromGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.READZONE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedZoneIds())
          .then(
            success => {
              this.refreshZones();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.READZONEINSTANCE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedZoneInstanceIds())
          .then(
            success => {
              this.refreshZoneInstances();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  showGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.READZONE: {
        this
          .mGroupService
          .getAllZonesInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.READZONEINSTANCE: {
        this
          .mGroupService
          .getAllZoneInstancesInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  addZoneClicked(): void {
    this.mEntity = new Zone();
    this.mEntityName = null;
    this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;
    this.mEntityTypeDialogComponent.showDialog();
  }

  editSelectedZoneClicked(): void {
    if (this.isSingleZoneSelected()) {
      this.editZone(this.getSelectedZones()[0]);
    }
  }

  duplicateSelectedZoneClicked(): void {
    if (this.isSingleZoneSelected()) {
      this
        .mZoneService
        .getZoneById(this.getSelectedZones()[0].id)
        .then(
          response => {
            response.id = null;
            if (response.properties != null) {
              response.properties.forEach(p => p.id = null);
            }
            if (response.functions != null) {
              response.functions.forEach(f => f.id = null);
            }

            this.mEntity = response;
            this.mEntityName = null;
            this.mEntityTypeDialogComponent.showDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  deleteSelectedZonesClicked(): void {
    if (this.isAnyZonesSelected()) {
      this.mDeleteFlag = 1; //1 = Zones
      this.mConfirmDeleteDialog.show("zone type", this.getSelectedZoneNames());
    }
  }

  editZone(zone: Zone): void {
    this
      .mZoneService
      .getZoneById(zone.id)
      .then(
        response => {
          this.mEntity = response;
          this.mEntityName = response.name;
          this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;

          if (response.parent != null) {
              this.mEntityTypeDialogComponent.entityParent = this.mAvailableEntityTypes.find(p => p.id == response.parent.id);
          }

          this.mEntityTypeDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityType(): void {
    if (this.mEntity.id == null) {
      //ID = null, this is a new Zone... Create it
      this
        .mZoneService
        .createZone(this.mEntity)
        .then(
          response => {
            this.refreshZones();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID <> null, this is an existing Zone... Update it
      this
        .mZoneService
        .updateZone(this.mEntity)
        .then(
          response => {
            this.refreshZones();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }





  addZoneInstanceClicked(): void {
    //Create a new Instance to be modeled in the Entity Instance Dialog
    this.mInstance = new ZoneInstance();
    this.mInstanceName = null;

    //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
    this.mEntityInstanceDialogComponent.setOriginalInstanceData([]);
    this.refreshTypesReloadDataRefreshLocationsAndShowDialog();
  }

  editSelectedZoneInstanceClicked(): void {
    if (this.isSingleZoneInstanceSelected()) {
      this.editZoneInstance(this.getSelectedZoneInstances()[0]);
    }
  }

  duplicateSelectedZoneInstanceClicked(): void {
    if (this.isSingleZoneInstanceSelected()) {
      this
      .mZoneService
      .getZoneInstanceById(this.getSelectedZoneInstances()[0].id)
      .then(
        response => {
          //Set the Instance to be modeled in the Entity Instance Dialog to the Instance returned...
          this.mInstance = response;

          //Set the Instance Name value so that it gets properly displayed in the Dialog Header
          this.mInstanceName = response.name;

          //Reset the IDs of the Instance and all the Instance Data so that it gets treated as new on the Server
          this.mInstance.id = null;
          this.mInstance.uuid = null;
          if (this.mInstance.data != null) {
            this.mInstance.data.forEach(d => d.id = null);
          }

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataRefreshLocationsAndShowDialog();
        },
        error => this.handleError(error)
      );
    }
  }

  deleteSelectedZoneInstancesClicked(): void {
    if (this.isAnyZoneInstancesSelected()) {
      this.mDeleteFlag = 2; //2 = Zone Instances
      this.mConfirmDeleteDialog.show("zone", this.getSelectedZoneInstanceNames());
    }
  }

  editZoneInstance(zoneInstance: ZoneInstance): void {
    this
      .mZoneService
      .getZoneInstanceById(zoneInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          //Set the Instance Name which is displayed in the Dialog Header
          this.mInstanceName = response.name;

          // Set the original Instance Data being displayed by the EntityInstanceDialogComponent
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(this.mInstance.data);

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataRefreshLocationsAndShowDialog();
        },
        error => this.handleError(error)
      );
  }

  async refreshTypesReloadDataRefreshLocationsAndShowDialog(): Promise<void> {
    //Create a List of the available Zones that the Instance can be a type of from the List already obtained
    this.mAvailableEntityTypes = await this.mZoneService.getAllZones();

    //Find/Set the Entity Type the Instance is the type of
    this.mInstanceEntityType = this.mInstance.zone == null? null : this.mAvailableEntityTypes.find(entityType => entityType.id == this.mInstance.zone.id);
    if (this.mInstanceEntityType != null) {
      this.mInstanceEntityType.functions = this.mInstance.zone.functions;
      this.mInstanceEntityType.properties = this.mInstance.zone.properties;
    }

    //Refresh the available and selected Locations
    this.mSelectedLocation = null;
    this
      .mLocationService
      .getAllLocationInstances()
      .then(
        response => {
          //Set the possible Location values so that it can be used by the EntityInstanceDialogComponent
          this.mAvailableLocations = response;

          //Figure out if a Location was previously selected for the Zone, get the correct version and store it
          this.mSelectedLocation = this.mInstance == null || this.mInstance.locationInstance == null? null : this.mAvailableLocations.find(location => location.id == this.mInstance.locationInstance.id);

          // Need to detect the changes here so that the "mInstanceEntityType" reference update gets passed to the EntityInstanceDialogComponent being shown below
          this.cdr.detectChanges();

          //Refresh the Instance Data, loading the existing values in for the Instance being edited
          this.mEntityInstanceDialogComponent.refreshData();

          //Show the Entity Instance Dialog
          this.mEntityInstanceDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityInstance(): void {
    //Set the values to the Zone Instance to be created
    this.mInstance.zone = this.mInstanceEntityType;
    this.mInstance.locationInstance = this.mSelectedLocation;

    if (this.mInstance.id == null) {
      //ID is null, Zone Instance does not exist!  Create it...
      this
        .mZoneService
        .createZoneInstance(this.mInstance)
        .then(
          response => {
            this.refreshZoneInstances();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID is not null, Zone Instance exists!  Update it...
      this
        .mZoneService
        .updateZoneInstance(this.mInstance)
        .then(
          response => {
            this.refreshZoneInstances();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  selectedEntityTypeChanged(entityType: Zone): void {
    this.mInstanceEntityType = entityType;
    if (this.mInstanceEntityType != null && this.mInstanceEntityType.properties == null) {
      this
        .mZoneService
        .getZoneById(this.mInstanceEntityType.id)
        .then(
          foundEntityType => {
            this.mInstanceEntityType.functions = foundEntityType.functions;
            this.mInstanceEntityType.properties = foundEntityType.properties;
            this.mEntityInstanceDialogComponent.refreshData();
          },
          error => this.handleError(error)
        );
    } else {
      this.mEntityInstanceDialogComponent.refreshData();
    }
  }

  selectedLocationChanged(location: LocationInstance): void {
    this.mSelectedLocation = location;
  }

  refreshInstanceData(): void {
    this
      .mZoneService
      .getZoneInstanceById(this.mInstance.id)
      .then(
        response => {
          this.mInstance.data = response.data;
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(response.data);
          this.mEntityInstanceDialogComponent.refreshData();
        },
        error => this.handleError(error)
      );
  }

  deleteConfirmed(): void {
    if (this.mDeleteFlag == 1) {  //1 = Zones
      this
        .mZoneService
        .deleteZones(this.getSelectedZoneIds())
        .then(
          response => {
            this.refreshZones();
          },
          error => this.handleError(error)
        );
    } else if (this.mDeleteFlag == 2) { //2 = Zone Instances
      this
        .mZoneService
        .deleteZoneInstances(this.getSelectedZoneInstanceIds())
        .then(
          response => {
            this.refreshZoneInstances();
          },
          error => this.handleError(error)
        );
    }
  }

 

  handleError(error: any): void {
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    } 
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

   /******************** FILTER FUNCTIONS FOR ZONE *****************/

   private refreshFilterOptionsForZones(): void {
    this.filterOptionForZones = [
      REMOVE_FILTER,
      new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
    ];
   }

   // Called by <app-filter-bar> whenever the active filter or its value changes.
   onZoneFilterChanged(option: FilterOption): void {
    this.selectedFilterOptionForZone = option;
    this.currentPageForZones = 1;
    this.refreshZones();
   }

   filterStringChangedZones(pageNumber?: number): void {
    if(!pageNumber) { pageNumber = 1;}
    this.currentPageForZones = pageNumber;
    this.pageChangedZone(pageNumber);
   }

    /******************** FILTER FUNCTIONS FOR ZONE INSTANCES *****************/

    private refreshFilterOptionsForZoneInstances(): void{
      this.filterOptionForZoneInstances = [
        REMOVE_FILTER,
        new FilterOption({ column: "name", label: "Name", type: FilterType.STRING }),
        new FilterOption({ column: "zone.name", label: "Type", type: FilterType.STRING }),
      ];
    }

    // Called by <app-filter-bar> whenever the active filter or its value changes.
    onZoneInstanceFilterChanged(option: FilterOption): void {
      this.selectedFilterOptionForZoneInstance = option;
      this.currentPageForZoneInstances = 1;
      this.refreshZoneInstances();
    }

    filterStringChangedZoneInstances(pageNumber?: number): void {
      if(!pageNumber) { pageNumber = 1;}
      this.currentPageForZoneInstances = pageNumber;
      this.pageChangedZoneInstance(pageNumber);
    }



   /******************** PAGINATOR FUNCTIONS FOR ZONES  *****************/

  pageChangedZone(pageNumber: number): void {
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1){
      return;
    }

    if(this.currentPageForZones == pageNumber) {
      return;
    }

    this.currentPageForZones = pageNumber;
    this.refreshZones();
  }

  private async refreshZones(): Promise<void> {
    this.spinnerService.show();
    let pagedData: PagedResults<Zone>;
    try {
      pagedData = await this.mZoneService.getAllZonesForPaging((this.currentPageForZones - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.selectedFilterOptionForZone.apiValue);
    } catch (error) {
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }

    this.updatePagedZones(pagedData);
    this.spinnerService.hide();
  }

  
   /******************** PAGINATOR FUNCTIONS FOR ZONE INSTANCES *****************/

   pageChangedZoneInstance(pageNumber: number): void {
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    if(this.currentPageForZoneInstances == pageNumber) {
      return;
    }

    this.currentPageForZoneInstances = pageNumber;
    this.refreshZoneInstances();
   }

   private async refreshZoneInstances(): Promise<void> {
    this.spinnerService.show();
    let pagedData: PagedResults<ZoneInstance>;
    try {
      pagedData = await this.mZoneService.getAllZoneInstancesForPagingWithFilters((this.currentPageForZoneInstances - 1) * this.selectedPageSizeValueForInstances, this.selectedPageSizeValueForInstances, "ASC", this.activeFilterOptionsForZoneInstance);
    } catch (error) {
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }

    this.updatePagedZoneInstances(pagedData);
    this.spinnerService.hide();
   }

   /******************** RESULTS PER PAGE FUNCTIONS  *****************/

   manageResultPerPageClickedForType(value: number): void{
    this.selectedPageSizeValueForTypes = value;
    this.currentPageForZones = 1;
    this.refreshZones();
   }

   manageResultPerPageClickedForInstance(value: number): void{
    this.selectedPageSizeValueForInstances = value;
    this.currentPageForZoneInstances = 1;
    this.refreshZoneInstances();
   }

  /*  
*  Handling the Right click event only on Zones Instance Name Column
*/
  fetchFunctionList(event: MouseEvent, zoneInstance: ZoneInstance): void {
    // Stop the default handling of right click event
    event.preventDefault();
    this.showContextMenu = true;
    this.showGeneralDialog = false;

    // Set the x & y co-ordinates for the context menu to open up
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Empty the function List if any element still present
    this.functionList = []

    // Send the request to get the Zones Instance details using the ID
    this
      .mZoneService
      .getZoneInstanceById(zoneInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the drop-down to the Instance that was returned
          this.mInstance = response;

          // Create the function List for the context menu from the response received 
          try {
            if (response.data && Array.isArray(response.zone.functions) && (response.zone.functions).length > 0) {
              this.functionList = response.zone.functions;

            } else {
              // Input a dummy

              this.functionList = INFRA_DUMMY_FUNCTION;
            }
          }
          catch (error) {
            this.handleError(error);
          }
        },
        error => this.handleError(error)
      );

  }

  onItemClicked(item: any) {
    // Handle the click in the dropdown menu item here 
    this.selectedFunction = item;

    if (item.id != -1) {
      // Display the Dialog Box for confirmation
      this.showGeneralDialog = true;
    }

    // Hide the Context Menu/Dropdown
    this.showContextMenu = false;

  }



}

class ZoneUI {
  mSelected = false;
  mZone: Zone;
}

class ZoneInstanceUI {
  mSelected = false;
  mZoneInstance: ZoneInstance;
}